// Pan Control Component - Allows moving sketch objects within viewport
// Organized for Supabase Migration

import React, { useState } from 'react';
import { Move, Target, ArrowUp, ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';

export const PanControl = ({ 
  currentSketch, 
  getCurrentParams, 
  onParamsUpdate,
  viewportWidth,
  viewportHeight,
  viewportScale
}) => {
  const [panMode, setPanMode] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [showPanGuide, setShowPanGuide] = useState(false);

  const handlePanMove = (direction, amount = 10) => {
    if (!panMode) return;
    
    const currentParams = getCurrentParams();
    if (!currentParams) return;

    const newOffset = { ...panOffset };
    
    switch (direction) {
      case 'up':
        newOffset.y -= amount;
        break;
      case 'down':
        newOffset.y += amount;
        break;
      case 'left':
        newOffset.x -= amount;
        break;
      case 'right':
        newOffset.x += amount;
        break;
      default:
        return;
    }

    setPanOffset(newOffset);
    applyPanOffset(currentParams, newOffset);
  };

  const applyPanOffset = (params, offset) => {
    if (!params) return;
    
    const newParams = { ...params };
    
    // Apply offset to coordinate-based parameters
    Object.keys(newParams).forEach(key => {
      if (key.includes('X') || key.includes('x')) {
        if (typeof newParams[key] === 'number') {
          const newValue = newParams[key] + offset.x;
          newParams[key] = Math.max(0, Math.min(viewportWidth, newValue));
        }
      }
      if (key.includes('Y') || key.includes('y')) {
        if (typeof newParams[key] === 'number') {
          const newValue = newParams[key] + offset.y;
          newParams[key] = Math.max(0, Math.min(viewportHeight, newValue));
        }
      }
    });

    // Update each parameter individually
    if (onParamsUpdate) {
      Object.keys(newParams).forEach(key => {
        onParamsUpdate(key, newParams[key]);
      });
    }
  };

  const resetPan = () => {
    setPanOffset({ x: 0, y: 0 });
    const currentParams = getCurrentParams();
    if (currentParams && onParamsUpdate) {
      onParamsUpdate(currentParams);
    }
  };

  const centerSketch = () => {
    const currentParams = getCurrentParams();
    if (!currentParams) return;

    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;
    
    // Find current center of sketch
    let currentCenterX = 0, currentCenterY = 0;
    let coordCount = 0;
    
    Object.keys(currentParams).forEach(key => {
      if (key.includes('X') || key.includes('x')) {
        if (typeof currentParams[key] === 'number') {
          currentCenterX += currentParams[key];
          coordCount++;
        }
      }
      if (key.includes('Y') || key.includes('y')) {
        if (typeof currentParams[key] === 'number') {
          currentCenterY += currentParams[key];
        }
      }
    });

    if (coordCount > 0) {
      currentCenterX /= coordCount;
      currentCenterY /= coordCount;
      
      const offsetX = centerX - currentCenterX;
      const offsetY = centerY - currentCenterY;
      
      setPanOffset({ x: offsetX, y: offsetY });
      applyPanOffset(currentParams, { x: offsetX, y: offsetY });
    }
  };

  return (
    <div className="bg-blue-50 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-blue-800 flex items-center gap-2">
          <Move className="w-4 h-4" />
          Pan Control
        </h4>
        <button
          onClick={() => setPanMode(!panMode)}
          className={`px-3 py-1 text-xs rounded-full transition-colors ${
            panMode 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {panMode ? 'Active' : 'Enable'}
        </button>
      </div>

      {panMode && (
        <div className="space-y-3">
          {/* Directional Controls */}
          <div className="grid grid-cols-3 gap-2">
            <div></div>
            <button
              onClick={() => handlePanMove('up')}
              className="p-2 bg-blue-100 hover:bg-blue-200 rounded flex items-center justify-center"
            >
              <ArrowUp className="w-4 h-4 text-blue-600" />
            </button>
            <div></div>
            
            <button
              onClick={() => handlePanMove('left')}
              className="p-2 bg-blue-100 hover:bg-blue-200 rounded flex items-center justify-center"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
            </button>
            
            <button
              onClick={centerSketch}
              className="p-2 bg-green-100 hover:bg-green-200 rounded flex items-center justify-center"
              title="Center sketch in viewport"
            >
              <Target className="w-4 h-4 text-green-600" />
            </button>
            
            <button
              onClick={() => handlePanMove('right')}
              className="p-2 bg-blue-100 hover:bg-blue-200 rounded flex items-center justify-center"
            >
              <ArrowRight className="w-4 h-4 text-blue-600" />
            </button>
            
            <div></div>
            <button
              onClick={() => handlePanMove('down')}
              className="p-2 bg-blue-100 hover:bg-blue-200 rounded flex items-center justify-center"
            >
              <svg className="w-4 h-4 text-blue-600 transform rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>

          {/* Fine Control */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handlePanMove('up', 5)}
              className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
            >
              Fine Up
            </button>
            <button
              onClick={() => handlePanMove('down', 5)}
              className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
            >
              Fine Down
            </button>
            <button
              onClick={() => handlePanMove('left', 5)}
              className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
            >
              Fine Left
            </button>
            <button
              onClick={() => handlePanMove('right', 5)}
              className="px-2 py-1 text-xs bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
            >
              Fine Right
            </button>
          </div>

          {/* Reset and Guide */}
          <div className="flex gap-2">
            <button
              onClick={resetPan}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
            <button
              onClick={() => setShowPanGuide(!showPanGuide)}
              className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 rounded"
            >
              {showPanGuide ? 'Hide' : 'Show'} Guide
            </button>
          </div>

          {/* Pan Guide */}
          {showPanGuide && (
            <div className="bg-blue-100 rounded p-3 text-xs text-blue-800">
              <p className="font-medium mb-1">Pan Control Guide:</p>
              <ul className="space-y-1 text-xs">
                <li>• Use arrow buttons to move sketch in 4 directions</li>
                <li>• Fine controls for 5-pixel precision</li>
                <li>• Center button automatically centers the sketch</li>
                <li>• Reset button returns to original position</li>
                <li>• Sketch stays within viewport boundaries</li>
              </ul>
            </div>
          )}

          {/* Status */}
          <div className="text-xs text-gray-600">
            <p>Offset: X: {panOffset.x}, Y: {panOffset.y}</p>
            <p>Mode: {panMode ? 'Active' : 'Inactive'}</p>
          </div>
        </div>
      )}
    </div>
  );
};
